import { inject, injectable } from 'inversify';
import { PJSONPOptions } from 'pjsonp';
import { RouteEntry, WayPoint } from '../interfaces/route/RouteEntry';
import {
  LocationStation,
  PositionStation,
  RouteProfile,
  SpeedProfile,
  TimeProfile
} from '../interfaces/state-events/RoutingRequest';
import {
  AddressStation,
  RoutingRequestData
} from '../interfaces/state-events/RoutingRequest';
import {
  Credentials,
  ResponseError
} from '../interfaces/webservices/core-api-v2/General';
import {
  BaseCalculateRouteRequest,
  CalculateRouteAddressRequest,
  CalculateRouteLocationRequest,
  CalculateRoutePositionRequest
} from '../interfaces/webservices/core-api-v2/Request/GeoServices';
import {
  RouteAddress,
  RouteIsoCountryCode
} from '../interfaces/webservices/core-api-v2/Request/GeoServices';
import {
  RouteLocation,
  RoutePosition
} from '../interfaces/webservices/core-api-v2/Request/GeoServices';
import { GeoServicesResult } from '../interfaces/webservices/core-api-v2/Response/GeoServicesResult';
import { Routing } from '../interfaces/webservices/Routing';
import { isValidTimeFormat } from './guards';
import { RoutingStorage } from './storage/RoutingStorage';
import { ContainerTypes } from './types/ContainerTypes';
import pjsonp from './vendor/pjsonp';

interface RouteContainer {
  request: BaseCalculateRouteRequest &
    (
      | CalculateRouteAddressRequest
      | CalculateRouteLocationRequest
      | CalculateRoutePositionRequest);
  url: string;
}

@injectable()
export class RequestRouting implements Routing {
  public static getAddressStations(
    input: RoutingRequestData
  ): RouteAddress & RouteIsoCountryCode {
    const result: any = {};

    const stations = input.stations.slice(0) as AddressStation[];
    const start = stations.shift() as AddressStation;
    const end = stations.pop() as AddressStation;

    result.RouteStartZip = start.zip;
    result.RouteStartCity = start.city;
    result.RouteStartCityPart = start.cityPart;
    result.RouteStartHouseNo = '';
    result.RouteStartStreet = start.streetNo;
    result.RouteStartIsoCountryCode = start.isoCountry;

    result.RouteEndZip = end.zip;
    result.RouteEndCity = end.city;
    result.RouteEndCityPart = end.cityPart;
    result.RouteEndHouseNo = '';
    result.RouteEndStreet = end.streetNo;
    result.RouteEndIsoCountryCode = end.isoCountry;

    for (let i = 0; i < 3; i++) {
      const station = stations[i] || {};
      const index = i + 1;
      result[`RouteStation${index}Zip`] = station.zip || '';
      result[`RouteStation${index}City`] = station.city || '';
      result[`RouteStation${index}CityPart`] = station.cityPart || '';
      result[`RouteStation${index}HouseNo`] = '';
      result[`RouteStation${index}Street`] = station.streetNo || '';
      result[`RouteStation${index}IsoCountryCode`] = station.isoCountry || '';
    }
    return result;
  }

  public static getLocationStations(
    input: RoutingRequestData
  ): RouteLocation & RouteIsoCountryCode {
    const result: any = {};

    const stations = input.stations.slice(0) as LocationStation[];
    const start = stations.shift() as LocationStation;
    const end = stations.pop() as LocationStation;

    result.RouteStart = start.location;
    result.RouteStartIsoCountryCode = start.isoCountry;

    result.RouteEnd = end.location;
    result.RouteEndIsoCountryCode = end.isoCountry;

    for (let i = 0; i < 3; i++) {
      const station = stations[i] || {};
      const index = i + 1;
      result[`RouteStation${index}`] = station.location || '';
      result[`RouteStation${index}IsoCountryCode`] = station.isoCountry || '';
    }
    return result;
  }

  public static getPositionStations(
    input: RoutingRequestData
  ): RoutePosition & RouteIsoCountryCode {
    const result: any = {};

    const stations = input.stations.slice(0) as PositionStation[];
    const start = stations.shift() as PositionStation;
    const end = stations.pop() as PositionStation;

    result.RouteStartLocX = start.position.lng;
    result.RouteStartLocY = start.position.lat;
    result.RouteStartIsoCountryCode = start.isoCountry;

    result.RouteEndLocX = end.position.lng;
    result.RouteEndLocY = end.position.lat;
    result.RouteEndIsoCountryCode = end.isoCountry;

    for (let i = 0; i < 3; i++) {
      const station = stations[i] || {};
      const index = i + 1;
      const position = station.position || { lng: '', lat: '' };
      result[`RouteStation${index}LocX`] = position.lng || '';
      result[`RouteStation${index}LocY`] = position.lat || '';
      result[`RouteStation${index}IsoCountryCode`] = station
        ? station.isoCountry
        : '';
    }
    return result;
  }

  public static getRequestData(input: RoutingRequestData): RouteContainer {
    let stations: (RouteAddress | RouteLocation | RoutePosition) &
      RouteIsoCountryCode;
    let service: string;

    let [RouteTimeHour, RouteTimeMinute] = ['0', '0'];
    if (isValidTimeFormat(input.estimationTime)) {
      [RouteTimeHour, RouteTimeMinute] = input.estimationTime
        .split(':')
        .map((n: string) => n);
    }

    switch (this.getStationType(input)) {
      case 'Address':
        stations = this.getAddressStations(input);
        service = 'CalculateRouteAddress';
        break;
      case 'Location':
        stations = this.getLocationStations(input);
        service = 'CalculateRouteLocation';
        break;
      case 'Position':
        stations = this.getPositionStations(input);
        service = 'CalculateRoutePosition';
        break;
      default:
        throw new Error('Invalid API request. Stations are unknown.');
    }

    const request: BaseCalculateRouteRequest = {
      ...this.credentials,
      IsoLocale: input.isoLocale,
      RouteMap: 'false',
      RouteTime: input.timeProfile || TimeProfile.Arrival,
      RouteTimeHour: RouteTimeHour || '0',
      RouteTimeMinute: RouteTimeMinute || '0',
      CarType: input.speedProfile || SpeedProfile.Car,
      RouteOptimization: input.routeProfile || RouteProfile.Economical,
      CoordFormatOut: 'GEODECIMAL_POINT',
      CoordFormatIn: 'GEODECIMAL_POINT',
      Addition: ''
    };

    return {
      request: {
        ...stations,
        ...request
      },
      url: this.url + service
    };
  }

  public static getStationType(
    input: RoutingRequestData
  ): 'Address' | 'Location' | 'Position' {
    if (input.stations.length > 5) {
      throw new Error('Maximum routing stations are set to 5.');
    }
    let stationType = null;
    for (const station of input.stations) {
      if (stationType === null) {
        stationType = station.type;
      } else if (stationType !== station.type) {
        throw new Error('Station types must have the same type.');
      }
    }

    if (stationType === null) {
      throw new Error('Station type is unknown!');
    }

    return stationType as 'Address' | 'Location' | 'Position';
  }

  public static handleServerErrors(result: ResponseError): void {
    if (result.InternalError) {
      throw new Error(result.InternalError);
    } else if (result.PublicReport) {
      throw new Error(result.PublicReport);
    }
  }

  public static generateRouteEntry(
    result: GeoServicesResult,
    fitBounds = true
  ): RouteEntry {
    const entry: RouteEntry = {
      type: 'Route',
      fitBounds,
      position: { lat: 0, lng: 0 }
    };

    if (result.Route) {
      entry.distance = result.Route.Distance;
      entry.duration = result.Route.Duration;

      entry.position = { lat: 0, lng: 0 };
      entry.polyline = result.Route.PolyLine.map(point => {
        const lat: number = parseFloat(point.Y);
        const lng: number = parseFloat(point.X);
        entry.position.lat += lat;
        entry.position.lng += lng;
        return [lat, lng];
      }) as Array<[number, number]>;
      entry.position.lat /= result.Route.PolyLine.length;
      entry.position.lng /= result.Route.PolyLine.length;
      entry.waypoints = result.Route.WayPointDescription.map(
        wp =>
          ({
            type: 'WayPoint',
            nextDuration: wp.NextDuration,
            nextDistance: wp.NextDistance,
            directionText: wp.Direction,
            trackSectionText: wp.TrackSection,
            description: wp.Description,
            compass: wp.Compass,
            navigationCommand: wp.NavImage,
            position: {
              lat: parseFloat(wp.GeoCoord.Y),
              lng: parseFloat(wp.GeoCoord.X)
            }
          } as WayPoint)
      );
    }

    if (entry.waypoints && entry.waypoints.length) {
      entry.waypoints.splice(entry.waypoints.length - 1, 1);
    }

    if (result.PublicReport) {
      entry.error = result.PublicReport;
    }

    return entry;
  }

  public static isValid(input: RoutingRequestData) {
    if (input.stations.length > 5) {
      throw new Error('Maximum routing stations are fix defined to 5.');
    }
    let stationType = null;
    for (const station of input.stations) {
      if (stationType === null) {
        stationType = station.type;
      } else if (stationType !== station.type) {
        throw new Error('Station types must have the same type.');
      }
    }
  }

  public static get url(): string {
    return `${ym.options.rootUrl}api_js/V2/GeoServices.asmx/`;
  }

  public static get credentials(): Credentials {
    return {
      SystemPartner: ym.options.name || '',
      SecurityID: ym.options.securityID || '',
      Channel: ym.options.channel || ''
    };
  }

  public static get jsonpOptions(): PJSONPOptions {
    return {
      prefix: 'YMFF__'
    };
  }

  @inject(ContainerTypes.RoutingStorage)
  public storage!: RoutingStorage;

  public async calculateRoute(
    input: RoutingRequestData,
    fitBounds = true
  ): Promise<RouteEntry> {
    const { jsonpOptions } = RequestRouting;

    const container: RouteContainer = RequestRouting.getRequestData(input);
    const result: GeoServicesResult = await pjsonp(
      container.url,
      container.request,
      jsonpOptions
    );
    RequestRouting.handleServerErrors(result);
    const routingEntry = RequestRouting.generateRouteEntry(result, fitBounds);

    this.storage.routeEntry = routingEntry;
    return routingEntry;
  }
}
